// components/models/NanoBananaProControlPanel.tsx
import React, { useCallback, memo } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import { NanoBananaImageSize, NanoBananaProParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const EMPTY_TEXTAREA_STYLE_OVERRIDES = {};
const IMAGE_SIZE_OPTIONS = ['4K', '2K', '1K'];
const SELECT_FULL_WIDTH_STYLE = { width: '100%' };

export const getDefaultNanoBananaProParams = (): NanoBananaProParams => ({
  prompt: '',
  seed: -1,
  imageSize: '1K',
  enable_google_search: false,
});

const NanoBananaProControlPanel: React.FC = () => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as NanoBananaProParams | undefined
  );

  const updateParam = useCallback((name: string, value: string | boolean) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[NanoBananaProControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        }
      };
    });

    if (typeof value === 'string') {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value_length: value.length,
        model_name: diffusionModel,
        source_component: 'NanoBananaProControlPanel'
      });
    } else {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value,
        model_name: diffusionModel,
        source_component: 'NanoBananaProControlPanel'
      });
    }
  }, [kernel]);

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    updateParam(name, value);
  }, [updateParam]);

  const handleImageSizeChange = useCallback((val: string) => {
    updateParam('imageSize', val as NanoBananaImageSize);
  }, [updateParam]);

  const handleGoogleSearchCheckedChange = useCallback((checked: boolean) => {
    updateParam('enable_google_search', checked);
  }, [updateParam]);

  if (!params) return null;

  return (
    <>
      <div>
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what you want to change..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>
      
      <div className="mt-4">
        <Select
          id="imageSize"
          label="Image Size"
          value={params.imageSize}
          options={IMAGE_SIZE_OPTIONS}
          onChange={handleImageSizeChange}
          style={SELECT_FULL_WIDTH_STYLE}
        />
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-white mb-1">Google Search Assist</label>
        <Switch
          checked={!!params.enable_google_search}
          onCheckedChange={handleGoogleSearchCheckedChange}
          labelLeft="Off"
          labelRight="On"
        />
        <p className="text-xs text-[#4D4D4D] mt-1">Allow web search.</p>
      </div>

    </>
  );
};

export default memo(NanoBananaProControlPanel);

